import { useEffect, useRef } from "react";

/**
 * A React hook that suspends rendering until the `enabled` flag becomes `true`.
 *
 * This hook is intended for use with React Suspense. When `enabled` is `false`, it throws a promise,
 * causing the component to suspend until `enabled` becomes `true`. Once `enabled` is `true`, the promise
 * is resolved and the component continues rendering.
 *
 * Optionally, a `logName` can be provided for debugging or logging purposes.
 *
 * @param enabled - A boolean flag indicating whether to enable rendering. If `false`, the hook will suspend.
 * @param logName - (Optional) A string used for logging or debugging purposes.
 *
 * @throws Promise<void> - Throws a promise to suspend rendering when `enabled` is `false`.
 *
 * @example
 * ```tsx
 * function MyComponent({ isReady }: { isReady: boolean }) {
 *   useSuspendedUntilEnabled(isReady, "MyComponent");
 *   return <div>Loaded!</div>;
 * }
 * ```
 */
const useSuspendedUntilEnabled = (enabled: boolean, logName?: string) => {
    const enabledRef = useRef(enabled);
    const intervalRef = useRef<number | undefined>(undefined);
    const resolveRef = useRef<(() => void) | undefined>(undefined);

    // Update ref when enabled changes
    useEffect(() => {
        enabledRef.current = enabled;
    }, [enabled]);

    // Initialize promise ref immediately if not enabled
    const suspendedPromiseRef = useRef<Promise<void> | undefined>(
        !enabled
            ? new Promise<void>(resolve => {
                  resolveRef.current = resolve;
              })
            : undefined,
    );

    // Create or recreate the suspension promise when enabled status changes
    useEffect(() => {
        // Clean up any existing interval
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = undefined;
        }

        if (!enabled) {
            // Set up polling only if not already polling
            if (!intervalRef.current) {
                // Create promise if it doesn't exist
                if (!suspendedPromiseRef.current) {
                    suspendedPromiseRef.current = new Promise<void>(resolve => {
                        resolveRef.current = resolve;
                    });
                }

                // Set up polling to check when we're enabled
                intervalRef.current = window.setInterval(() => {
                    if (enabledRef.current && resolveRef.current) {
                        clearInterval(intervalRef.current);
                        intervalRef.current = undefined;
                        resolveRef.current();
                        resolveRef.current = undefined;
                    }
                }, 50);
            }
        } else {
            // If we're enabled, resolve any existing promise
            if (resolveRef.current) {
                resolveRef.current();
                resolveRef.current = undefined;
            }
            suspendedPromiseRef.current = undefined;
        }

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [enabled, logName]);

    // The key part - throw the promise if not enabled and we have a promise
    if (!enabled && suspendedPromiseRef.current) {
        // eslint-disable-next-line @typescript-eslint/only-throw-error
        throw suspendedPromiseRef.current;
    }
};

export { useSuspendedUntilEnabled };
